import React, { useEffect } from "react";
import { EditOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { Button, Form, Image, Input, Popconfirm } from "antd";
import TextArea from "antd/es/input/TextArea";

import ButtonWrapper from "../../../components/reuseComp/ButtonWrapper";

interface ContentItem {
  title: string;
  desc: string;
}

interface ChallengesProps {
  heading?: string;
  subtext?: string;
  content?: ContentItem[];
}

interface Props {
  isEditing: boolean;
  setIsEditing: (v: boolean) => void;
  handleCancel: () => void;
  handleSave: () => void;
  onDelete: (index: number, field: "Challenges") => void;
  form: any;
  challengeData?: ChallengesProps;
  loading?: boolean;
  mode: "list" | "add" | "edit";
  setMode: (value: "list" | "add" | "edit") => void;
  editingIndex: number | null;
  setEditingIndex: (value: number | null) => void;
}

const IndustryChallenges: React.FC<Props> = ({
  isEditing,
  setIsEditing,
  handleCancel,
  handleSave,
  form,
  challengeData,
  onDelete,
  loading,
  mode,
  setMode,
  editingIndex,
  setEditingIndex,
}) => {
  useEffect(() => {
    if (!challengeData) return;

    form.resetFields(["challenges", "content"]);

    if (mode === "list") {
      form.setFieldsValue({
        challenges: {
          heading: challengeData.heading || "",
          subtext: challengeData.subtext || "",
          content: challengeData.content || [],
        },
      });
    }

    if (mode === "add") {
      form.setFieldsValue({
        challenges: {
          content: [{ title: "", desc: "" }],
        },
      });
    }

    if (mode === "edit" && editingIndex !== null) {
      const item = challengeData.content?.[editingIndex];
      if (item) {
        form.setFieldsValue({
          challenges: {
            content: [{ title: item.title, desc: item.desc }],
          },
        });
      }
    }
  }, [mode, editingIndex, challengeData]);

  return (
    <div>
      {isEditing && (
        <div className="bg-[#F9F9F9] p-5 rounded-xl mb-4">
          <Form layout="vertical" form={form}>
            <Form.Item
              label="Heading"
              name={["challenges", "heading"]}
              rules={[{ required: true, message: "Enter heading" }]}
            >
              <Input placeholder="enter heading..."/>
            </Form.Item>

            <Form.Item
              label="Subtext"
              name={["challenges", "subtext"]}
              rules={[{ required: true, message: "Enter subtext" }]}
            >
              <TextArea rows={3} placeholder="enter subtext..."/>
            </Form.Item>
          </Form>

          <div className="flex gap-3 mt-4">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>
            <ButtonWrapper
              onClick={() => {
                setIsEditing(false);
                handleCancel();
              }}
            >
              Cancel
            </ButtonWrapper>
          </div>
        </div>
      )}

      {mode === "list" && !isEditing && (
        <div className="rounded-[20px] overflow-hidden">
          <div className="hidden sm:block">
            <table className="min-w-full">
              <thead>
                <tr className="bg-primary text-white">
                  <th className="py-3 px-4 text-left">Heading</th>
                  <th className="py-3 px-4 text-left">Subtext</th>
                  <th className="py-3 px-4 text-right">Action</th>
                </tr>
              </thead>

              <tbody className="bg-[#F9F9F9]">
                {challengeData?.heading || challengeData?.subtext ? (
                  <tr className="border-b">
                    <td className="px-4 py-3">{challengeData?.heading}</td>
                    <td className="px-4 py-3">{challengeData?.subtext}</td>
                    <td className="px-4 py-3 text-right">
                      <EditOutlined
                        className="text-primary text-xl cursor-pointer"
                        onClick={() => setIsEditing(true)}
                      />
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan={3} className="text-center py-6">
                      <Button type="dashed" onClick={() => setIsEditing(true)}>
                        Add Data
                      </Button>
                    </td>
                  </tr>
                )}
                <tr>
                  <td colSpan={3} className="px-4 py-3 text-right">
                    <Button
                      type="dashed"
                      className="text-primary font-semibold"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setMode("add");
                        setEditingIndex(null);
                      }}
                    >
                      Add Sections
                    </Button>
                  </td>
                </tr>

                <tr>
                  <td colSpan={3} className="p-4">
                    <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="px-4 py-2 text-left">Title</th>
                          <th className="px-4 py-2 text-left">Description</th>
                          <th className="px-4 py-2 text-right">Action</th>
                        </tr>
                      </thead>

                      <tbody>
                        {challengeData?.content?.length ? (
                          challengeData.content.map((item, index) => (
                            <tr key={index} className="border-t">
                              <td className="px-4 py-2">{item.title}</td>
                              <td className="px-4 py-2">{item.desc}</td>
                              <td className="px-4 py-2 text-right">
                                <div className="flex justify-end gap-4 text-lg">
                                  <EditOutlined
                                    className="cursor-pointer text-primary"
                                    onClick={() => {
                                      setMode("edit");
                                      setEditingIndex(index);
                                    }}
                                  />
                                  <Popconfirm
                                    title="Are you sure to delete this?"
                                    onConfirm={() =>
                                      onDelete(index, "Challenges")
                                    }
                                  >
                                    <DeleteOutlined className="cursor-pointer text-red-500" />
                                  </Popconfirm>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={3}
                              className="px-4 py-6 text-center text-gray-500"
                            >
                              No sections added
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="sm:hidden space-y-4">
            <div className="bg-[#F9F9F9] p-4 rounded-xl shadow-sm">
              <div className="space-y-2">
                <div>
                  <div>
                    <b className="text-primary">Heading:</b>
                  </div>
                  <p>{challengeData?.heading || "—"}</p>
                </div>

                <div>
                  <div>
                    <b className="text-primary">Subtext:</b>
                  </div>
                  <p>{challengeData?.subtext || "—"}</p>
                </div>

                <div className="flex justify-end text-primary">
                  <EditOutlined
                    className="cursor-pointer text-[20px]"
                    onClick={() => setIsEditing(true)}
                  />
                </div>
              </div>
            </div>

            <div className="text-right">
              <Button
                type="dashed"
                className="text-primary font-semibold"
                icon={<PlusOutlined />}
                onClick={() => {
                  setMode("add");
                  setEditingIndex(null);
                }}
              >
                Add Sections
              </Button>
            </div>

            {challengeData?.content?.length ? (
              challengeData.content.map((item, index) => (
                <div
                  key={index}
                  className="bg-[#F9F9F9] p-4 rounded-xl shadow-sm"
                >
                  <div className="space-y-2">
                    <div>
                      <div>
                        <p className="font-semibold text-primary">Title:</p>
                      </div>
                      <p className="">{item.title}</p>
                    </div>

                    <div>
                      <div className="font-semibold text-primary">Description:</div>
                      <p className="">{item.desc}</p>
                    </div>
                  </div>

                  <div className="flex justify-end gap-4 text-[20px] mt-2 text-primary">
                    <EditOutlined
                      className="cursor-pointer"
                      onClick={() => {
                        setMode("edit");
                        setEditingIndex(index);
                      }}
                    />
                    <Popconfirm
                      title="Are you sure to delete this?"
                      onConfirm={() => onDelete(index, "Challenges")}
                    >
                      <DeleteOutlined className="cursor-pointer text-red-500" />
                    </Popconfirm>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-6">
                No sections added
              </div>
            )}
          </div>
        </div>
      )}

      {mode !== "list" && !isEditing && (
        <div className="bg-[#F9F9F9] p-5 rounded-xl">
          <Form layout="vertical" form={form}>
            <Form.List name={["challenges", "content"]}>
              {(fields) => (
                <>
                  {fields.map((field) => (
                    <div key={field.key} className="border p-4 rounded mb-4">
                      <Form.Item
                        label="Title"
                        name={[field.name, "title"]}
                        rules={[{ required: true, message: "Enter title" }]}
                      >
                        <Input placeholder="enter title..."/>
                      </Form.Item>

                      <Form.Item
                        label="Description"
                        name={[field.name, "desc"]}
                        rules={[
                          { required: true, message: "Enter description" },
                        ]}
                      >
                        <TextArea rows={2} placeholder="enter description..."/>
                      </Form.Item>
                    </div>
                  ))}
                </>
              )}
            </Form.List>
          </Form>

          <div className="flex gap-3 mt-4">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>
            <ButtonWrapper
              onClick={() => {
                handleCancel();
                setMode("list");
                setEditingIndex(null);
              }}
            >
              Cancel
            </ButtonWrapper>
          </div>
        </div>
      )}
    </div>
  );
};

export default IndustryChallenges;
